import React, { createContext, useContext, useMemo, useState, useEffect } from "react";

const BlockingContext = createContext(null);

export function BlockingProvider({ children, defaultContent }) {
  const [count, setCount] = useState(0);
  const [message, setMessage] = useState();
  const [content, setContent] = useState(null);

  const api = useMemo(() => ({
    isBlocking: count > 0,
    start: (arg) => {
      setCount((c) => c + 1);

      // Si viene vacío, dejamos que el overlay use el default
      if (arg == null) return;

      // Si viene string, lo usamos como mensaje
      if (typeof arg === "string") {
        setMessage(arg);
        setContent(null);
        return;
      }

      // Si viene un objeto { message, content }
      if (typeof arg === "object" && !React.isValidElement(arg)) {
        if (arg.message !== undefined) setMessage(arg.message);
        if (arg.content !== undefined) setContent(arg.content);
        return;
      }

      // Si viene un elemento React (<MiLoading/>)
      if (React.isValidElement(arg)) {
        setContent(arg);
        return;
      }
    },
    stop: () => {
      setCount((c) => Math.max(0, c - 1));
      // opcional: limpiar cuando termina todo
      // if (count <= 1) { setMessage(undefined); setContent(null); }
    },
    message,
    content,
  }), [count, message, content]);

  return (
    <BlockingContext.Provider value={api}>
      {children}
      <BlockingOverlay defaultContent={defaultContent} />
    </BlockingContext.Provider>
  );
}

export function useBlocking() {
  const ctx = useContext(BlockingContext);
  if (!ctx) throw new Error("useBlocking debe usarse dentro de BlockingProvider");
  return ctx;
}

function BlockingOverlay({ defaultContent }) {
  const { isBlocking, message, content } = useBlocking();

  useEffect(() => {
    if (!isBlocking) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isBlocking]);

  if (!isBlocking) return null;

  const fallback =
    defaultContent ??
    (
      <div style={{ textAlign: "center" }}>
        <div style={{ marginBottom: 10 }}>Cargando…</div>
        <div style={{ fontSize: 14 }}>{message ?? "Ejecutando…"}</div>
      </div>
    );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999999,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          background: "white",
          borderRadius: 8,
          padding: 16,
          boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {content ?? fallback}
      </div>
    </div>
  );
}